import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, X, Activity, ChevronDown, ChevronUp, AlertCircle, Loader2, Sparkles, CheckSquare, Square } from "lucide-react";
import api, { apiError } from "../lib/api";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const ICON_OPTIONS = ["Calculator", "Cpu", "GraduationCap", "Factory", "UtensilsCrossed", "PartyPopper", "Scissors", "HeartPulse", "Dumbbell", "ShoppingBag", "Megaphone", "Briefcase", "Users", "Scale", "Home", "Car", "Truck", "Plane", "Newspaper", "Rocket", "Store", "Brush", "Baby", "Tractor", "Leaf", "HandHeart", "Landmark", "Shield", "PawPrint", "Palette", "Target", "Laptop"];

function IndustryForm({ initial, onSave, onClose }) {
  const [f, setF] = useState({
    name: "", description: "", icon: "Briefcase", display_order: 0, active: true,
    ...initial,
    subsText: (initial?.subcategories || []).filter((s) => s.active !== false).map((s) => s.name).join(", "),
  });
  const submit = async (e) => {
    e.preventDefault();
    const existing = initial?.subcategories || [];
    const names = f.subsText.split(",").map((x) => x.trim()).filter(Boolean);
    const subcategories = names.map((n) => {
      const old = existing.find((s) => s.name === n);
      return { name: n, slug: old?.slug, active: true };
    });
    for (const old of existing) {
      if (!names.includes(old.name)) subcategories.push({ ...old, active: false });
    }
    try {
      await onSave({ name: f.name, description: f.description, icon: f.icon,
        display_order: Number(f.display_order) || 0, active: f.active, subcategories });
      onClose();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-xl my-8 space-y-4" data-testid="industry-form-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{initial?.id ? "Edit industry" : "Add industry"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </div>
        <div><label className="text-xs font-semibold text-slate-500">Name</label>
          <input data-testid="industry-form-name" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></div>
        <div><label className="text-xs font-semibold text-slate-500">Description (one line)</label>
          <input data-testid="industry-form-description" className={inputCls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="text-xs font-semibold text-slate-500">Icon</label>
            <select data-testid="industry-form-icon" className={inputCls} value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })}>
              {ICON_OPTIONS.map((i) => <option key={i}>{i}</option>)}
            </select></div>
          <div><label className="text-xs font-semibold text-slate-500">Display order</label>
            <input data-testid="industry-form-order" type="number" className={inputCls} value={f.display_order} onChange={(e) => setF({ ...f, display_order: e.target.value })} /></div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input data-testid="industry-form-active" type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Active
            </label></div>
        </div>
        <div><label className="text-xs font-semibold text-slate-500">Subcategories (comma-separated — remove one to deactivate it)</label>
          <textarea data-testid="industry-form-subs" className={`${inputCls} min-h-[110px]`} value={f.subsText} onChange={(e) => setF({ ...f, subsText: e.target.value })} /></div>
        <button data-testid="industry-form-save-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-all">Save industry</button>
      </form>
    </div>
  );
}

function SuggestTagsModal({ industry, onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [applied, setApplied] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [limit, setLimit] = useState(60);
  const [minRel, setMinRel] = useState(55);
  const [includeTagged, setIncludeTagged] = useState(false);

  const fetchSuggestions = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await api.post(`/admin/industries/${industry.slug}/suggest-tags`,
        { limit, min_relevance: minRel, include_already_tagged: includeTagged });
      setData(r.data);
      // pre-tick suggestions with relevance >= 70 by default
      setChecked(new Set((r.data.suggestions || []).filter(s => s.relevance >= 70).map(s => s.command_id + "|" + s.industry_slug)));
    } catch (e) { toast.error(apiError(e), { duration: 10000 }); onClose(); }
    setLoading(false);
  };

  useEffect(() => { fetchSuggestions(); /* eslint-disable-next-line */ }, []);

  const toggleOne = (key) => setChecked(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleAll = () => {
    const all = (data?.suggestions || []).map(s => s.command_id + "|" + s.industry_slug);
    setChecked(prev => prev.size === all.length ? new Set() : new Set(all));
  };

  const apply = async () => {
    if (!data) return;
    const selected = data.suggestions.filter(s => checked.has(s.command_id + "|" + s.industry_slug));
    if (!selected.length) { toast.error("Tick at least one suggestion to apply"); return; }
    setApplying(true);
    try {
      const payload = { items: selected.map(s => ({ command_id: s.command_id, industry_slug: s.industry_slug, relevance: s.relevance })) };
      const r = await api.post(`/admin/industries/${industry.slug}/apply-tags`, payload);
      toast.success(`✓ Applied ${r.data.applied} tag${r.data.applied === 1 ? "" : "s"}` + (r.data.skipped_already_mapped ? ` (${r.data.skipped_already_mapped} already mapped, skipped)` : ""), { duration: 6000 });
      setApplied(true);
      onApplied && onApplied();
    } catch (e) { toast.error(apiError(e), { duration: 10000 }); }
    setApplying(false);
  };

  const groupedByDisplay = () => {
    const groups = {};
    (data?.suggestions || []).forEach(s => {
      if (!groups[s.industry_display]) groups[s.industry_display] = [];
      groups[s.industry_display].push(s);
    });
    return groups;
  };
  const groups = groupedByDisplay();

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-3xl my-8 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="suggest-tags-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Sparkles size={18} className="text-emerald-700" /> Auto-suggest tags — {industry.name}</h3>
            <p className="text-xs text-slate-500 mt-1">Claude Sonnet classifies untagged commands against this industry and its sub-industries. Review, uncheck anything that doesn't fit, apply.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </div>

        <div className="flex flex-wrap items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs">
          <label className="font-semibold text-slate-500">Commands to classify:</label>
          <input type="number" min="10" max="200" value={limit} onChange={(e) => setLimit(parseInt(e.target.value) || 60)}
            className="w-20 border border-slate-200 rounded px-2 py-1" data-testid="suggest-limit-input" />
          <label className="font-semibold text-slate-500 ml-3">Min relevance:</label>
          <input type="number" min="0" max="95" value={minRel} onChange={(e) => setMinRel(parseInt(e.target.value) || 0)}
            className="w-16 border border-slate-200 rounded px-2 py-1" data-testid="suggest-minrel-input" />
          <label className="inline-flex items-center gap-1.5 ml-3">
            <input type="checkbox" checked={includeTagged} onChange={(e) => setIncludeTagged(e.target.checked)} className="w-3.5 h-3.5 accent-emerald-600" />
            Include commands already tagged here
          </label>
          <div className="flex-1" />
          <button onClick={fetchSuggestions} disabled={loading} data-testid="suggest-rerun-btn"
            className="inline-flex items-center gap-1.5 font-bold text-emerald-700 hover:bg-emerald-50 px-2.5 py-1 rounded">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Re-run
          </button>
        </div>

        {loading && (
          <div className="py-10 text-center text-sm text-slate-500">
            <Loader2 size={28} className="animate-spin mx-auto mb-2 text-emerald-600" />
            Classifying up to {limit} commands… this takes 10-30 seconds.
          </div>
        )}

        {!loading && data && data.suggestions.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
            <strong>No suggestions found.</strong> The LLM looked at {data.candidates_considered} commands and found none it was confident enough to tag at relevance ≥ {minRel}.
            Try lowering the min relevance, enabling "Include already tagged", or increasing the pool size.
          </div>
        )}

        {!loading && data && data.suggestions.length > 0 && (
          <>
            <div className="flex items-center justify-between text-sm">
              <div><strong>{data.suggestions.length}</strong> suggestions from {data.candidates_considered} commands considered · <strong>{checked.size}</strong> ticked</div>
              <button onClick={toggleAll} className="text-xs font-bold text-emerald-700 hover:underline" data-testid="suggest-toggle-all">
                {checked.size === data.suggestions.length ? "Untick all" : "Tick all"}
              </button>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 max-h-[55vh] overflow-y-auto">
              {Object.entries(groups).map(([display, items]) => (
                <div key={display}>
                  <div className="bg-slate-50 px-4 py-1.5 text-[11px] font-black uppercase tracking-wider text-slate-500">{display} · {items.length}</div>
                  {items.map(s => {
                    const key = s.command_id + "|" + s.industry_slug;
                    const isChecked = checked.has(key);
                    return (
                      <label key={key} className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer" data-testid={`suggest-row-${s.command_slug}`}>
                        <button type="button" onClick={() => toggleOne(key)} className="flex-shrink-0 mt-0.5 text-slate-400 hover:text-emerald-600">
                          {isChecked ? <CheckSquare size={16} className="text-emerald-600" /> : <Square size={16} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold text-emerald-700">/{s.command_slug}</span>
                            <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${
                              s.relevance >= 80 ? "bg-emerald-100 text-emerald-800" :
                              s.relevance >= 65 ? "bg-blue-100 text-blue-800" :
                              "bg-amber-100 text-amber-800"
                            }`}>rel {s.relevance}</span>
                            <span className="text-[11px] text-slate-500 truncate">{s.command_name} · {s.command_category}</span>
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5">{s.reason}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={onClose} className="font-bold text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={apply} disabled={applying || checked.size === 0 || applied} data-testid="suggest-apply-btn"
                className="inline-flex items-center gap-1.5 bg-emerald-600 text-white font-bold text-sm px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                {applying ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {applied ? "Applied" : `Apply ${checked.size} tag${checked.size === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


const HEALTH_META = {
  strong: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Strong" },
  good:   { color: "bg-blue-100 text-blue-800 border-blue-200",         label: "Good" },
  weak:   { color: "bg-amber-100 text-amber-800 border-amber-200",      label: "Weak" },
  empty:  { color: "bg-rose-100 text-rose-800 border-rose-200",         label: "Empty" },
};

function CoveragePanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("tagged"); // tagged | name | health
  const [showEmptySubs, setShowEmptySubs] = useState(false);
  const [suggestFor, setSuggestFor] = useState(null); // { slug, name }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/industries/coverage");
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  useEffect(() => { if (open && !data) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedRows = (() => {
    if (!data) return [];
    const rows = [...data.report];
    if (sortBy === "name") rows.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === "health") {
      const order = { empty: 0, weak: 1, good: 2, strong: 3 };
      rows.sort((a, b) => order[a.health] - order[b.health] || b.unique_tagged_commands - a.unique_tagged_commands);
    } else rows.sort((a, b) => b.unique_tagged_commands - a.unique_tagged_commands);
    return rows;
  })();

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="industry-coverage-panel">
      <button
        onClick={() => setOpen(!open)}
        data-testid="industry-coverage-toggle"
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <Activity size={18} className="text-emerald-700" />
          <div>
            <p className="font-bold text-sm">Industry tagging coverage</p>
            <p className="text-xs text-slate-500">Which industries have the most / least construction-style command mappings</p>
          </div>
        </div>
        {open ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4 space-y-3">
          {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Analysing…</div>}
          {!loading && data && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="font-bold text-slate-500">Active commands:</span> <strong>{data.total_active_commands}</strong>
                <span className="font-bold text-slate-500">· Universal (apply to all):</span> <strong>{data.universal_commands}</strong>
                <span className="font-bold text-slate-500">· Industries:</span> <strong>{data.industries_count}</strong>
                <div className="flex-1" />
                <label className="text-xs font-semibold text-slate-500">Sort:</label>
                <select
                  data-testid="industry-coverage-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white"
                >
                  <option value="tagged">Most tagged first</option>
                  <option value="health">Weakest first (needs attention)</option>
                  <option value="name">A → Z</option>
                </select>
                <button onClick={load} className="text-xs font-bold text-emerald-700 hover:underline">Refresh</button>
              </div>

              <div className="rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold">Industry</th>
                      <th className="text-center px-3 py-2 font-bold">Health</th>
                      <th className="text-right px-3 py-2 font-bold" title="Unique commands tagged either at the parent or any sub-industry slug">Tagged</th>
                      <th className="text-right px-3 py-2 font-bold" title="Commands with industries.industry_slug = parent slug">Parent</th>
                      <th className="text-right px-3 py-2 font-bold" title="Commands tagged at any sub-industry slug">Sub</th>
                      <th className="text-right px-3 py-2 font-bold">Subs</th>
                      <th className="text-right px-3 py-2 font-bold" title="Average relevance score (0-100) across all tagged mappings">Avg rel.</th>
                      <th className="text-right px-3 py-2 font-bold">AI</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedRows.map((r) => {
                      const health = HEALTH_META[r.health] || HEALTH_META.weak;
                      const emptySubs = r.sub_slugs.filter((s) => (r.sub_distribution[s] || 0) === 0);
                      return (
                        <>
                          <tr key={r.id} className={`hover:bg-slate-50 ${!r.active ? "opacity-50" : ""}`} data-testid={`coverage-row-${r.slug}`}>
                            <td className="px-3 py-2">
                              <div className="font-bold">{r.name}</div>
                              <div className="text-[11px] text-slate-400 font-mono">{r.slug}</div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className={`inline-block text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${health.color}`}>{health.label}</span>
                            </td>
                            <td className="px-3 py-2 text-right font-bold">{r.unique_tagged_commands}</td>
                            <td className="px-3 py-2 text-right text-slate-600">{r.direct_tag_count}</td>
                            <td className="px-3 py-2 text-right text-slate-600">{r.sub_tag_count}</td>
                            <td className="px-3 py-2 text-right text-slate-500 text-xs">{r.subcategories_count}</td>
                            <td className="px-3 py-2 text-right text-slate-500 text-xs">{r.avg_relevance || "—"}</td>
                            <td className="px-3 py-2 text-right">
                              <button
                                data-testid={`suggest-btn-${r.slug}`}
                                onClick={() => setSuggestFor({ slug: r.slug, name: r.name })}
                                title="Use Claude to classify untagged commands against this industry and its sub-industries"
                                className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded">
                                <Sparkles size={11} /> Auto-suggest
                              </button>
                            </td>
                          </tr>
                          {showEmptySubs && emptySubs.length > 0 && (
                            <tr key={`${r.id}-subs`} className="bg-amber-50/60">
                              <td colSpan={8} className="px-3 py-2 text-[11px] text-amber-900">
                                <strong>Sub-industries with 0 tagged commands:</strong>{" "}
                                {emptySubs.map((s) => <span key={s} className="inline-block font-mono bg-white border border-amber-200 rounded px-1.5 py-0.5 mr-1">{s}</span>)}
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={showEmptySubs} onChange={(e) => setShowEmptySubs(e.target.checked)} className="w-3.5 h-3.5 accent-emerald-600" />
                  Show untagged sub-industry slugs (good targets to seed)
                </label>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 leading-relaxed">
                <div className="flex items-start gap-2">
                  <AlertCircle size={14} className="text-emerald-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>How to read this:</strong> A command needs an entry in its <code className="bg-slate-200 px-1 rounded">industries</code> array
                    pointing to this industry (or one of its sub-industries). Commands tagged with a parent OR any sub-slug now ALL show up
                    for the industry in the dictionary — this diagnostic shows how dense that tagging is.
                    <strong> Weak</strong> or <strong>Empty</strong> rows mean the dictionary will fall back to universal commands, which users
                    will see as "irrelevant prompts". Add tags from the Commands admin tab.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {suggestFor && (
        <SuggestTagsModal
          industry={suggestFor}
          onClose={() => setSuggestFor(null)}
          onApplied={() => { setData(null); load(); }}
        />
      )}
    </div>
  );
}

export function AdminIndustries() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/industries").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (editing?.id) await api.put(`/admin/industries/${editing.id}`, payload);
    else await api.post("/admin/industries", payload);
    toast.success("Saved");
    load();
  };

  const toggleActive = async (r) => {
    await api.put(`/admin/industries/${r.id}`, { name: r.name, description: r.description, icon: r.icon,
      display_order: r.display_order, active: !r.active, subcategories: r.subcategories });
    load();
  };

  return (
    <div className="max-w-3xl space-y-4">
      <CoveragePanel />
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-400">{rows.length} industries · assign shortcuts to industries from the Commands tab</p>
        <button data-testid="admin-add-industry-btn" onClick={() => setEditing({})}
          className="bg-slate-900 text-white text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-slate-800 transition-all">Add industry</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
            <div className="min-w-0">
              <p className={`font-semibold text-sm ${r.active ? "" : "text-slate-400 line-through"}`}>{r.name}</p>
              <p className="text-xs text-slate-400 truncate mt-0.5">{r.subcategories.filter((s) => s.active !== false).length} subcategories · {r.description}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`industry-toggle-${r.slug}`} onClick={() => toggleActive(r)}
                className={`text-xs font-bold px-2.5 py-1 rounded-full ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                {r.active ? "Active" : "Inactive"}
              </button>
              <button data-testid={`industry-edit-${r.slug}`} onClick={() => setEditing(r)} className="text-slate-400 hover:text-emerald-600 p-1.5"><Pencil size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing !== null && <IndustryForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
